import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Heart, Flame, Star, Trophy, Target, BookOpen, Wind, Sparkles, 
  Calendar, CheckCircle2, Clock, LogOut, RefreshCw, AlertTriangle, 
  Phone, MessageCircle, Loader2, Brain, Bot, CalendarPlus, PenLine
} from 'lucide-react';
import AIStructuredExercises from './AIStructuredExercises';
import YouthAICompanion from './YouthAICompanion';
import YouthSecureMessaging from './YouthSecureMessaging';
import YouthSelfScheduling from './YouthSelfScheduling';

/**
 * YoungAdultDashboard - Dashboard for 16-17 year olds with full features
 * Includes: AI Companion, Secure Messaging, Self-Scheduling, Journal
 */

const EMOJI_MOODS = [
  { value: 1, emoji: '😢', label: 'Really struggling' },
  { value: 2, emoji: '😟', label: 'Anxious' },
  { value: 3, emoji: '😕', label: 'Not great' },
  { value: 4, emoji: '😐', label: 'Okay' },
  { value: 5, emoji: '🙂', label: 'Good' },
  { value: 6, emoji: '😊', label: 'Great' },
  { value: 7, emoji: '😄', label: 'Amazing!' },
];

const YoungAdultDashboard = ({ onLogout, backendUrl }) => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [showMoodCheckin, setShowMoodCheckin] = useState(false);
  const [selectedMood, setSelectedMood] = useState(null);
  const [submittingMood, setSubmittingMood] = useState(false);
  
  const clientId = sessionStorage.getItem('youth_client_id');
  const firstName = sessionStorage.getItem('youth_first_name') || 'there';
  const ageTier = sessionStorage.getItem('youth_age_tier');
  const features = JSON.parse(sessionStorage.getItem('youth_features') || '{}');
  const token = sessionStorage.getItem('youth_token');

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${backendUrl}/api/youth-portal/dashboard?client_id=${clientId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [backendUrl, clientId, token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const submitMoodCheckin = async () => {
    if (!selectedMood) return;
    
    setSubmittingMood(true);
    try {
      const response = await fetch(`${backendUrl}/api/mood-checkins/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          client_id: clientId,
          mood_score: selectedMood.value,
          emotions: [selectedMood.label],
          source: 'youth_portal'
        })
      });
      
      if (response.ok) {
        setShowMoodCheckin(false);
        setSelectedMood(null);
        fetchDashboard();
      }
    } catch (error) {
      console.error('Error submitting mood:', error);
    } finally {
      setSubmittingMood(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('youth_token');
    sessionStorage.removeItem('youth_client_id');
    sessionStorage.removeItem('youth_first_name');
    sessionStorage.removeItem('youth_age_tier');
    sessionStorage.removeItem('youth_features');
    onLogout();
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-400 mx-auto" />
          <p className="mt-4 text-purple-200">Loading your space...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900" data-testid="young-adult-dashboard">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{getGreeting()}, {firstName}</h1>
              <p className="text-sm text-purple-300">Your wellness hub</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {dashboardData?.streak > 0 && (
              <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30">
                <Flame className="w-3 h-3 mr-1" />
                {dashboardData.streak} day streak
              </Badge>
            )}
            
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="text-gray-400 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-6 bg-white/5 backdrop-blur-sm border border-white/10 p-1 rounded-xl">
            <TabsTrigger value="home" className="text-xs data-[state=active]:bg-purple-500/30 data-[state=active]:text-white text-gray-400">
              <Heart className="w-4 h-4 mr-1" />
              Home
            </TabsTrigger>
            <TabsTrigger value="companion" className="text-xs data-[state=active]:bg-purple-500/30 data-[state=active]:text-white text-gray-400">
              <Bot className="w-4 h-4 mr-1" />
              AI Chat
            </TabsTrigger>
            <TabsTrigger value="messages" className="text-xs data-[state=active]:bg-purple-500/30 data-[state=active]:text-white text-gray-400">
              <MessageCircle className="w-4 h-4 mr-1" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="schedule" className="text-xs data-[state=active]:bg-purple-500/30 data-[state=active]:text-white text-gray-400">
              <CalendarPlus className="w-4 h-4 mr-1" />
              Schedule
            </TabsTrigger>
            <TabsTrigger value="exercises" className="text-xs data-[state=active]:bg-purple-500/30 data-[state=active]:text-white text-gray-400">
              <Brain className="w-4 h-4 mr-1" />
              Exercises
            </TabsTrigger>
            <TabsTrigger value="help" className="text-xs data-[state=active]:bg-red-500/30 data-[state=active]:text-white text-gray-400">
              <Phone className="w-4 h-4 mr-1" />
              Help
            </TabsTrigger>
          </TabsList>

          {/* HOME TAB */}
          <TabsContent value="home" className="space-y-6">
            {/* Mood Check-in */}
            <Card className="bg-gradient-to-br from-purple-600/80 to-pink-600/80 border-0 shadow-xl backdrop-blur-sm">
              <CardContent className="p-6 text-white">
                {!showMoodCheckin ? (
                  <div className="text-center">
                    <h3 className="text-xl font-semibold mb-2">How are you feeling?</h3>
                    <p className="text-purple-200 mb-4">Take a moment to check in with yourself</p>
                    <Button 
                      onClick={() => setShowMoodCheckin(true)}
                      className="bg-white/20 hover:bg-white/30 text-white border border-white/30"
                    >
                      Check In
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-center">Select your mood</h3>
                    <div className="flex justify-center gap-2 flex-wrap">
                      {EMOJI_MOODS.map((mood) => (
                        <button
                          key={mood.value}
                          onClick={() => setSelectedMood(mood)}
                          className={`text-3xl p-2 rounded-xl transition-all ${
                            selectedMood?.value === mood.value 
                              ? 'bg-white/30 scale-110 ring-2 ring-white' 
                              : 'hover:bg-white/20'
                          }`}
                        >
                          {mood.emoji}
                        </button>
                      ))}
                    </div>
                    {selectedMood && (
                      <p className="text-center text-purple-200">{selectedMood.label}</p>
                    )}
                    <div className="flex justify-center gap-3">
                      <Button 
                        variant="outline" 
                        onClick={() => { setShowMoodCheckin(false); setSelectedMood(null); }}
                        className="border-white/30 text-white hover:bg-white/20"
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={submitMoodCheckin}
                        disabled={!selectedMood || submittingMood}
                        className="bg-white text-purple-600 hover:bg-purple-50"
                      >
                        {submittingMood ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardContent className="p-4 text-center">
                  <Star className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{dashboardData?.gamification?.points || 0}</p>
                  <p className="text-sm text-gray-400">Points</p>
                </CardContent>
              </Card>
              <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardContent className="p-4 text-center">
                  <Flame className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{dashboardData?.streak || 0}</p>
                  <p className="text-sm text-gray-400">Day Streak</p>
                </CardContent>
              </Card>
              <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardContent className="p-4 text-center">
                  <Trophy className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-white">{dashboardData?.gamification?.badges?.length || 0}</p>
                  <p className="text-sm text-gray-400">Badges</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-4">
              <Card 
                className="bg-white/5 backdrop-blur-sm border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() => setActiveTab('companion')}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                    <Bot className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-white">AI Companion</p>
                    <p className="text-sm text-gray-400">Chat about anything</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="bg-white/5 backdrop-blur-sm border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() => setActiveTab('messages')}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-teal-500 flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Message Therapist</p>
                    <p className="text-sm text-gray-400">Secure messaging</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="bg-white/5 backdrop-blur-sm border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() => setActiveTab('schedule')}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                    <CalendarPlus className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Book Session</p>
                    <p className="text-sm text-gray-400">Schedule appointment</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card 
                className="bg-white/5 backdrop-blur-sm border-white/10 cursor-pointer hover:bg-white/10 transition-colors"
                onClick={() => setActiveTab('exercises')}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-white">Guided Exercises</p>
                    <p className="text-sm text-gray-400">CBT & mindfulness</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Goals */}
            {dashboardData?.goals?.length > 0 && (
              <Card className="bg-white/5 backdrop-blur-sm border-white/10">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-green-400" />
                    Your Goals
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {dashboardData.goals.slice(0, 3).map((goal, idx) => (
                    <div key={idx} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-300">{goal.title}</span>
                        <span className="text-purple-400">{goal.progress || 0}%</span>
                      </div>
                      <Progress value={goal.progress || 0} className="h-2 bg-white/10" />
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* AI COMPANION TAB */}
          <TabsContent value="companion">
            <Card className="bg-white/5 backdrop-blur-sm border-white/10">
              <CardContent className="p-0">
                <YouthAICompanion 
                  clientId={clientId}
                  token={token}
                  backendUrl={backendUrl}
                  clientName={firstName}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* MESSAGES TAB */}
          <TabsContent value="messages">
            <YouthSecureMessaging 
              clientId={clientId}
              token={token}
              backendUrl={backendUrl}
            />
          </TabsContent>

          {/* SCHEDULE TAB */}
          <TabsContent value="schedule">
            <div className="bg-white rounded-xl">
              <YouthSelfScheduling 
                clientId={clientId}
                token={token}
                backendUrl={backendUrl}
              />
            </div>
          </TabsContent>

          {/* EXERCISES TAB */}
          <TabsContent value="exercises">
            <Card className="bg-white rounded-xl">
              <CardContent className="p-6">
                <AIStructuredExercises 
                  clientId={clientId}
                  token={token}
                  backendUrl={backendUrl}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* HELP TAB */}
          <TabsContent value="help" className="space-y-4">
            <Card className="bg-gradient-to-br from-red-600/80 to-orange-600/80 border-0">
              <CardContent className="p-6 text-center text-white">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">Need Help Right Now?</h2>
                <p className="text-red-100 mb-4">If you're in crisis, please reach out</p>
                <a href="tel:988">
                  <Button className="bg-white text-red-600 hover:bg-red-50 text-lg px-8 py-6">
                    <Phone className="w-5 h-5 mr-2" />
                    Call or Text 988
                  </Button>
                </a>
              </CardContent>
            </Card>

            <div className="grid gap-3">
              {[
                { name: '988 Suicide & Crisis Lifeline', phone: '988', desc: '24/7 support' },
                { name: 'Crisis Text Line', phone: 'Text HOME to 741741', desc: 'Text-based support' },
                { name: 'Teen Line', phone: '1-800-852-8336', desc: 'Teens helping teens' },
                { name: 'The Trevor Project', phone: '1-866-488-7386', desc: 'LGBTQ+ youth support' },
              ].map((resource, idx) => (
                <Card key={idx} className="bg-white/5 backdrop-blur-sm border-white/10">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-white">{resource.name}</p>
                      <p className="text-sm text-gray-400">{resource.desc}</p>
                    </div>
                    <Badge className="bg-purple-500/30 text-purple-200 border-0">
                      {resource.phone}
                    </Badge>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default YoungAdultDashboard;
